import { createFileRoute } from '@tanstack/react-router';
import { useForm, FormProvider } from 'react-hook-form';
import type { z as zod } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Trans } from '@lingui/macro';

import { Wrapper, CollectivePurchaseForm } from '@/components';
import { handleFormError } from '@/utils';
import {
  useCollectivePurchaseSuspense,
  useUpdateCollectivePurchase,
  getCollectivePurchaseQueryKey,
  getCollectivePurchasesQueryKey,
  getCollectivePurchaseSuspenseQueryOptions,
  useSustainabilityActions,
  useAudiences,
} from '@/api';
import { collectivePurchaseSchema } from '@/schemas';

export const Route = createFileRoute(
  '/_authenticated/collective_purchases/$collectivePurchaseId/edit',
)({
  loader: async ({
    context: { queryClient },
    params: { collectivePurchaseId },
  }) =>
    queryClient.ensureQueryData(
      getCollectivePurchaseSuspenseQueryOptions(Number(collectivePurchaseId)),
    ),
  component: EditCollectivePurchase,
});

function EditCollectivePurchase() {
  const collectivePurchaseId = Route.useParams({
    select: ({ collectivePurchaseId }) => Number(collectivePurchaseId),
  });
  const { data: collectivePurchase } =
    useCollectivePurchaseSuspense(collectivePurchaseId);
  const { queryClient } = Route.useRouteContext({
    select: ({ queryClient }) => ({ queryClient }),
  });
  const { mutateAsync } = useUpdateCollectivePurchase({
    mutation: {
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: getCollectivePurchasesQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: getCollectivePurchaseQueryKey(collectivePurchaseId),
        });
      },
    },
  });
  const { data } = useSustainabilityActions();
  const { sustainabilityActions = [] } = data ?? {};
  const { data: audienceData } = useAudiences();
  const { audiences = [] } = audienceData ?? {};

  const methods = useForm<zod.infer<typeof collectivePurchaseSchema>>({
    resolver: zodResolver(collectivePurchaseSchema),
    defaultValues: {
      title: collectivePurchase.title,
      description: collectivePurchase.description,
      url: collectivePurchase.url,
      sustainabilityActionIds: collectivePurchase?.sustainabilityActions?.map(
        (sustainabilityAction) => sustainabilityAction.id.toString(),
      ),
      audienceIds: collectivePurchase?.audiences?.map((audience) =>
        audience.id.toString(),
      ),
      registerable:
        (collectivePurchase as { registerable?: boolean }).registerable ?? true,
    },
  });

  const onSave = methods.handleSubmit(async (data) => {
    try {
      const response = await mutateAsync({
        id: collectivePurchase.id,
        data: {
          ...data,
          amount: null, // Field removed from form, set to null
          deadline: '', // Field removed from form, set to empty string
          tagList: [], // Field removed from form, set to empty array
        },
      });
      methods.reset({
        title: response.title,
        description: response.description,
        url: response.url,
        sustainabilityActionIds: response?.sustainabilityActions?.map(
          (sustainabilityAction) => sustainabilityAction.id.toString(),
        ),
        audienceIds: response?.audiences?.map((audience) =>
          audience.id.toString(),
        ),
        registerable:
          (response as { registerable?: boolean }).registerable ?? true,
      });
      toast.success(<Trans>Collective purchase updated successfully!</Trans>);
    } catch (error) {
      handleFormError(error, methods.setError);
    }
  });

  return (
    <Wrapper
      header={<Trans>Edit collective purchase</Trans>}
      backLink='/collective_purchases'
    >
      <FormProvider {...methods}>
        <CollectivePurchaseForm
          sustainabilityActions={sustainabilityActions}
          audiences={audiences}
          onSave={onSave}
        />
      </FormProvider>
    </Wrapper>
  );
}
